import { Box, Chip, Skeleton, Stack, Typography } from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import type { JSX } from "react";
import { Trans, useTranslation } from "react-i18next";

import { demandScoreTranslationKeys } from "./translations";

import { DemandScoreIcon, ImmoScoutIcon } from "@/assets";
import { CustomChip } from "@/components/customChip/CustomChip";
import { PanelHeader } from "@/components/panelHeader";
import { CurrentValuationState } from "@/pages/valuationPackage/valuationDetailsPage/currentValuationSlice";
import { ValuationReportState } from "@/pages/valuationReport/valuationReportPage/valuationReportSlice";
import { colors } from "@/theme/tokens/colors";

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const createStyles = (theme: Theme) => ({
  panelContainer: {
    display: "grid",
    backgroundColor: "white",
    padding: theme.analysisLayout.panelPadding,
    gap: theme.analysisLayout.panelGap,
  },
  headerSection: {
    display: "grid",
    gap: theme.analysisLayout.compactGap,
  },
  scoreGauge: {
    position: "relative",
    width: "124px",
    height: "66px",
    flexShrink: 0,
    alignSelf: "start",
  },
  scoreGaugeContent: {
    position: "absolute",
    left: "21px",
    top: "21px",
    display: "flex",
    width: "82px",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
  },
});

interface DemandScorePanelProps {
  valuationState: ValuationReportState | CurrentValuationState;
  isLoading?: boolean;
}

const demandScoreChipTypes = {
  VERY_LOW_DEMAND: "error",
  LOW_DEMAND: "warning",
  MODERATE_DEMAND: "warning",
  HIGH_DEMAND: "success",
  VERY_HIGH_DEMAND: "success",
} as const;

interface DemandScoreGaugeProps {
  color: string;
  isLoading: boolean;
  score: number;
  value: number | null;
  label: string;
}

const DemandScoreGauge = ({ color, isLoading, score, value, label }: DemandScoreGaugeProps): JSX.Element => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const normalizedScore = Math.max(0, Math.min(score, 100));

  return (
    <Box sx={styles.scoreGauge}>
      <Box
        component="svg"
        viewBox="0 0 124 66"
        aria-hidden="true"
        sx={{ display: "block", width: "124px", height: "66px", overflow: "visible" }}
      >
        <path
          d="M3 62 A59 59 0 0 1 121 62"
          fill="none"
          stroke={theme.semanticColors.border.subtle}
          strokeLinecap="round"
          strokeWidth="5"
        />
        <path
          d="M3 62 A59 59 0 0 1 121 62"
          fill="none"
          pathLength={100}
          stroke={color}
          strokeDasharray="100"
          strokeDashoffset={100 - normalizedScore}
          strokeLinecap="round"
          strokeWidth="5"
        />
      </Box>
      <Box sx={styles.scoreGaugeContent}>
        {isLoading ? (
          <Skeleton variant="text" width={40} height={29} />
        ) : (
          <Typography
            component="div"
            sx={{
              color: theme.semanticColors.text.primary,
              fontSize: theme.analysisLayout.demandScoreValueFontSize,
              fontWeight: 600,
              lineHeight: "28px",
            }}
            data-cy="demand-score-value"
          >
            {value ?? "-"}
          </Typography>
        )}
        <Typography
          component="div"
          sx={{
            color: theme.semanticColors.text.muted,
            fontSize: theme.analysisLayout.demandScoreLabelFontSize,
            lineHeight: "15px",
          }}
        >
          {label}
        </Typography>
      </Box>
    </Box>
  );
};

export const DemandScorePanel = ({ valuationState, isLoading = false }: DemandScorePanelProps): JSX.Element => {
  const { t } = useTranslation("DemandScorePanel");
  const theme = useTheme();
  const styles = createStyles(theme);

  const { address, demandScore } = valuationState;

  const demandScoreClass = demandScore?.demand_score_class ?? null;
  const demandScoreTranslationKey = demandScoreClass ? demandScoreTranslationKeys[demandScoreClass] : null;
  const demandScoreValue = demandScore?.value ?? null;
  const demandScoreLabel = demandScoreTranslationKey ? t(`${demandScoreTranslationKey}.label`) : null;
  const demandScoreChipType = demandScoreClass ? demandScoreChipTypes[demandScoreClass] : null;
  const demandScoreText = demandScoreTranslationKey ? t(`${demandScoreTranslationKey}.text`) : null;
  const locationLabel = [address?.town, address?.post_code].filter(Boolean).join(", ");
  const hasDemandScore =
    demandScoreValue !== null && demandScoreClass !== null && demandScoreText !== null && Boolean(locationLabel);

  const score = demandScoreValue ?? 0;

  const demandScoreColor = !hasDemandScore
    ? theme.semanticColors.border.subtle
    : score >= 61
      ? theme.palette.success.dark
      : score >= 41
        ? theme.semanticColors.completeness.medium
        : score >= 21
          ? theme.palette.warning.dark
          : theme.palette.error.dark;

  return (
    <Box sx={styles.panelContainer}>
      <Box sx={styles.headerSection}>
        <PanelHeader
          title={t("title")}
          icon={
            <DemandScoreIcon sx={{ fontSize: theme.analysisLayout.headerIconSize, color: colors.brand.brownDark }} />
          }
          rightContent={
            demandScoreLabel && demandScoreChipType ? (
              <CustomChip
                label={demandScoreLabel}
                chipType={demandScoreChipType}
                data-cy="demand-score-label"
                fontSize={theme.analysisLayout.customChipFontSize}
              />
            ) : undefined
          }
          tooltip={{ content: t("tooltip") }}
        />
      </Box>

      <Stack direction="row" gap={theme.analysisLayout.panelGap} alignItems="center">
        <DemandScoreGauge
          color={demandScoreColor}
          isLoading={isLoading}
          label={t("of100")}
          score={score}
          value={demandScoreValue}
        />
        <Box sx={{ display: "grid", rowGap: theme.analysisLayout.panelGap }}>
          <Typography>
            {hasDemandScore ? (
              <Trans
                ns="DemandScorePanel"
                i18nKey="text"
                values={{ location: locationLabel, demandText: demandScoreText }}
                components={{ bold: <strong key="bold" /> }}
              />
            ) : (
              ""
            )}
          </Typography>
          <Stack direction={"row"} spacing={"12px"} alignItems={"center"}>
            <Chip
              icon={<ImmoScoutIcon sx={{ width: "80px" }} />}
              variant="outlined"
              sx={{
                pl: "14px",
                borderColor: theme.themeColors.scoutSecondaryColor,
              }}
            />

            <Typography>{t("ImmoScout24Daten")}</Typography>
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};
